import React, { useEffect, useRef } from "react";
import { FormattedMessage } from "react-intl";
import { getYahooAdsUri } from "./yahooFeed";
import { toTitleCase } from "../commons/HelperFunctions";
import { useLanderConfig } from "../commons/ConfigContext";
import { sendYahooEvent, EventType } from "./yahooEvent";
import { RSItem, TermOrigin } from "./types";
import { trackImpression, trackClick } from "../commons/signals";
import SearchIcon from "@ux/icon/search";
import ChevronRightIcon from "@ux/icon/chevron-right";
import "@ux/icon/search/index.css";
import "@ux/icon/chevron-right/index.css";

interface YahooRSProps {
  items: RSItem[];
  maxRS?: number;
  headerMessageId?: string;
}

export const YahooRS: React.FC<YahooRSProps> = ({
  items,
  maxRS,
  headerMessageId,
}) => {
  const config = useLanderConfig();
  const rootDomain = config.landerConfig.domain.rootDomain;
  const displayItems = maxRS ? items.slice(0, maxRS) : items;
  const hasEventFired = useRef(false);

  useEffect(() => {
    if (displayItems.length > 0 && !hasEventFired.current) {
      hasEventFired.current = true;
      const rsTerms = displayItems
        .map((item) => toTitleCase(item.term))
        .join(",");
      // Positionally aligned with rsTerms, so reporting can attribute an
      // impression to the source that supplied that row (PARKING-5269).
      // All-or-nothing: a partly-stamped block would produce a list that no
      // longer lines up with rsTerms, which is worse than sending none.
      const rsOrigins = displayItems.every((item) => item.origin)
        ? displayItems.map((item) => item.origin).join(",")
        : "";
      sendYahooEvent(EventType.RS_LOAD, config.landerConfig, {
        rsTerms,
        ...(rsOrigins && { rsOrigins }),
      });
      trackImpression({ section: "related_search", widget: "rs_block" });
    }
  }, [config.landerConfig, displayItems]);

  const handleClick = (keyword: string, origin?: TermOrigin) => {
    trackClick({ section: "related_search", widget: "rs_link" });
    sendYahooEvent(EventType.KEYWORD_CLICK, config.landerConfig, {
      searchQuery: keyword,
      ...(origin && { rsOrigin: origin }),
    });
  };

  return (
    <div className="yahooRS">
      <div className="relatedTopicsHeader">
        <span>
          <FormattedMessage
            id={ headerMessageId || "Yahoo-RelatedSearchTopics" }
          />
        </span>
      </div>
      <ul>
        {displayItems.map((item, index) => {
          const titleCased = toTitleCase(item.term);
          return (
            <li key={ `yahoo-rs-item-${index}` }>
              <div className="iconContainer">
                <SearchIcon style={{ color: "black" }} height="32" width="32" />
              </div>
              <a
                href={ getYahooAdsUri({
                  qs: item.qs,
                  rootDomain,
                  searchQuery: item.searchQuery,
                  system: config.landerConfig.system,
                  customerId: config.landerConfig.customerId,
                  typeTag: config.landerConfig.yahoo?.typeTag,
                }) }
                onClick={ () => handleClick(titleCased, item.origin) }
              >
                {titleCased}
              </a>
              <div style={{ minWidth: "32px" }}>
                <ChevronRightIcon
                  style={{ color: "black" }}
                  height="32"
                  width="32"
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
